import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';

export interface ModalProps {
  /** Whether the modal is open. Controlled. */
  open: boolean;
  /** Called when the modal requests to close (Esc, scrim click, close button). */
  onClose: () => void;
  /** Modal title (rendered in the header; wired to aria-labelledby). Optional — some modals render their own header layout. */
  title?: ReactNode;
  /** Modal width in pixels. Default 480 (per SessionExpiredModal spec). */
  width?: number;
  /** Whether clicking the scrim closes the modal. Default true. Set false for must-decide flows (e.g. SessionExpired). */
  closeOnScrimClick?: boolean;
  /** Whether pressing Escape closes the modal. Default true. Set false for must-decide flows. */
  closeOnEsc?: boolean;
  /** Whether to render a close button (X) in the header. Default true; set false for must-decide flows. */
  showCloseButton?: boolean;
  /** Whether the scrim is blurred (`backdrop-filter: blur`). Default true. */
  blurScrim?: boolean;
  /** Modal body. */
  children: ReactNode;
  /** Optional sticky footer (e.g. primary/cancel buttons). */
  footer?: ReactNode;
  /** Optional extra className on the panel (e.g. for spec-specific surface variants). */
  className?: string;
}

/**
 * Centered overlay modal with scrim. Used by:
 *  - SessionExpiredModal (must-decide variant: closeOnEsc/closeOnScrimClick = false)
 *  - Confirmation modals (future)
 *  - Any flow that demands the user's focus over the entire app surface
 *
 * Differs from Drawer (right-side panel) in layout intent: Modal interrupts
 * the user's current task; Drawer augments it with context they can dismiss
 * casually.
 *
 * Accessibility:
 *  - role="dialog", aria-modal, aria-labelledby on the title (when provided)
 *  - Esc + scrim click close (configurable; SessionExpiredModal sets both off)
 *  - Focus trap: focus moves to the first focusable element on open, returns
 *    to the trigger element on close. Tab cycles within the panel.
 *  - Body scroll lock while open.
 *
 * Rendered via React portal to document.body so it escapes any stacking
 * context the trigger lives in.
 */
export function Modal({
  open,
  onClose,
  title,
  width = 480,
  closeOnScrimClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  blurScrim = true,
  children,
  footer,
  className,
}: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Body scroll lock + restore previously focused element on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open]);

  // Escape-to-close.
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  // On open, focus the first focusable element inside the panel.
  useEffect(() => {
    if (!open) return;
    // Tiny delay so React has time to mount children + their refs.
    const id = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Focus trap: keep Tab/Shift+Tab within the panel.
  const onPanelKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusables = Array.from(
      panel.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const onScrimClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!closeOnScrimClick) return;
      // Only close when the click is on the scrim itself, not bubbling from the panel.
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnScrimClick, onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      className={[styles.scrim, blurScrim ? styles.scrimBlurred : null].filter(Boolean).join(' ')}
      onClick={onScrimClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={[styles.panel, className].filter(Boolean).join(' ')}
        style={{ width: `${width}px`, maxWidth: '92vw' }}
        role="dialog"
        aria-modal="true"
        {...(title !== undefined ? { 'aria-labelledby': titleId } : {})}
        onKeyDown={onPanelKeyDown}
      >
        {(title !== undefined || showCloseButton) && (
          <header className={styles.header}>
            {title !== undefined ? (
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </header>
        )}

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
